import { makeComputationApiRepository } from "../../core/repositories/repositoryBuilders.js";
import AccountingDocumentService from "../shared/documentInfrastructure/AccountingDocumentService.js";
import AnnualTrend from "../../models/accounting/AnnualTrend.js";
import Forecast from "../../models/forecast/instance/Forecast.js";
import ForecastComputationResult from "../../models/forecast/instance/ForecastComputationResult.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import KeyFigureSeriesSpecification from "../../models/forecast/instance/KeyFigureSeriesSpecification.js";
import PeriodChangeAccountOperation from "../../models/keyFigure/accountOperations/PeriodChangeAccountOperation.js";

const profitKeyFigureId = "Profit";
const incomeBeforeFinancialKeyFigureId = "IncomeBeforeFinancial";

/**
 * Service responsible for all data exchange required by the business trend
 * document. This service coordinates other services and repositories.
 *
 * @param {session} session - The user's session. Used to get the
 *   current company, current period…
 */
export default class BusinessTrendDocumentService extends AccountingDocumentService {
	constructor({ session }) {
		super(...arguments);

		this._getCurrentYear = () => session.getPeriod().getYear();

		this._forecastRepository = makeComputationApiRepository({
			route: "Estimates",
			model: Forecast,
			session,
		});

		this._forecastSeriesRepository = makeComputationApiRepository({
			route: "Forecasts/#id/Series",
			model: ForecastComputationResult,
			session,
		});

		this._keyFigureService = getKeyFigureService();
	}

	async fetchAnnualTrendForForecast(forecast) {
		const keyFigures = await this._keyFigureService.getAllFromCache();

		const annualTrendKeyFigureIds = [
			profitKeyFigureId,
			incomeBeforeFinancialKeyFigureId,
		];

		const annualTrendKeyFigures = keyFigures.filter((keyfigure) =>
			annualTrendKeyFigureIds.includes(keyfigure.getId()),
		);

		const seriesSpecifications = annualTrendKeyFigures.map((keyFigure) =>
			KeyFigureSeriesSpecification.makeKeyFromModel({
				keyFigure,
				operationType: PeriodChangeAccountOperation.getOperationType(),
			}),
		);

		const forecastSeries = await this._forecastSeriesRepository.save({
			notify: false,
			model: seriesSpecifications,
			filter: { id: forecast.getId() },
			context: { keyFigures: annualTrendKeyFigures },
		});

		return this._getForecastAnnualTrendsData({
			currentYear: this._getCurrentYear(),
			forecast,
			forecastSeries,
		});
	}

	getPublishedForecasts({ isForecastingEnabled }) {
		if (!isForecastingEnabled) return Promise.resolve([]);
		return this._forecastRepository.get();
	}

	_getForecastAnnualTrendValues({
		periods,
		forecast,
		keyFigureForecastValues,
	}) {
		const values = new Array(periods.length).fill(0);

		const startPeriod = forecast.findFirstAvailableFiscalYearPeriod({
			fiscalYearPeriods: periods,
		});

		if (!keyFigureForecastValues || !startPeriod.isFound) {
			return {
				data: values,
				accumulatedData: values,
			};
		}

		const allForecastingPeriods = forecast.getAllPeriods();
		let forecastPeriodIndex = startPeriod.forecastPeriodIndex;
		let fiscalYearPeriodIndex = startPeriod.fiscalYearPeriodIndex;

		while (
			forecastPeriodIndex < allForecastingPeriods.length &&
			fiscalYearPeriodIndex < periods.length
		) {
			values[fiscalYearPeriodIndex++] =
				keyFigureForecastValues[forecastPeriodIndex++].getValue();
		}

		return {
			data: values,
			accumulatedData: this._accumulatedPeriodValues({
				periodValues: values,
			}),
		};
	}

	_getForecastAnnualTrendsData({ currentYear, forecast, forecastSeries }) {
		const currentYearMonths = currentYear.asMonths();

		const profitTrendValues = forecastSeries.getKeyFigureValues({
			keyFigureId: profitKeyFigureId,
			operationType: PeriodChangeAccountOperation.getOperationType(),
		});

		const incomeTrendValues = forecastSeries.getKeyFigureValues({
			keyFigureId: incomeBeforeFinancialKeyFigureId,
			operationType: PeriodChangeAccountOperation.getOperationType(),
		});

		const profitAnnualTrends = this._getForecastAnnualTrendValues({
			periods: currentYearMonths,
			forecast,
			keyFigureForecastValues: profitTrendValues,
		});

		const incomeAnnualTrends = this._getForecastAnnualTrendValues({
			periods: currentYearMonths,
			forecast,
			keyFigureForecastValues: incomeTrendValues,
		});

		return new AnnualTrend({
			profitTrend: profitAnnualTrends.data,
			accumulatedProfitTrend: profitAnnualTrends.accumulatedData,
			incomeTrend: incomeAnnualTrends.data,
			accumulatedIncomeTrend: incomeAnnualTrends.accumulatedData,
			periodLabels: [],
		});
	}

	_accumulatedPeriodValues({ periodValues }) {
		let sum = 0;
		return periodValues.map((value) => (sum += value));
	}
}
